import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  isStarterName,
  STARTERS,
  STARTER_NAMES,
  type Folder,
  type QuizSummary,
} from '@floorpoll/shared';
import { CodeCells } from '../../components/CodeCells.js';
import { FormError } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { folderApi } from '../../lib/folderApi.js';
import { quizApi } from '../../lib/quizApi.js';
import { track } from '../../lib/track.js';
import { useTourAnchor } from '../../lib/tour/anchors.js';
import { useTour } from '../../lib/tour/TourProvider.js';
import { useWorkspace } from '../../lib/workspace.js';
import { groupQuizzesByFolder, type FolderGroup } from './folderGrouping.js';

/** The value the folder selects use for "no folder", since a select carries strings. */
const UNFILED = '';

/**
 * A group's identity on the rail: its folder's id, or UNFILED for the
 * remainder. The rail's selection is one of these, or `null` for All.
 */
const groupKey = (group: FolderGroup) => group.folder?.folderId ?? UNFILED;

/** Story #13 — the dashboard. Folders and starters are #76. */
export function QuizList() {
  const { active, can } = useWorkspace();
  const { start: startGuide } = useTour();
  const navigate = useNavigate();
  // Held in variables rather than called inline: the form they belong to renders
  // inside a permission ternary, and a hook cannot live in one.
  const titleAnchor = useTourAnchor('polls-create-field');
  const submitAnchor = useTourAnchor('polls-create-submit');
  const [quizzes, setQuizzes] = useState<QuizSummary[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [title, setTitle] = useState('');
  const [starter, setStarter] = useState('');
  const [createIn, setCreateIn] = useState(UNFILED);
  const [folderName, setFolderName] = useState('');
  // `null` is All. Otherwise a group key — a folder's id, or UNFILED.
  const [filter, setFilter] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  // The rail swaps the trigger for the naming field and back, so closing the
  // field unmounts whatever had focus. Without this the caret lands on <body>
  // and a keyboard user loses their place in the rail entirely.
  const newFolderRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [addingFolder, setAddingFolder] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  const workspaceId = active?.workspaceId;
  const canWrite = can('quiz:write');
  // A folder can go away while its id is still sitting in the create form.
  const createInFolder = folders.some((f) => f.folderId === createIn) ? createIn : UNFILED;

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      // One query for the polls and one for the folders — per workspace, not
      // per poll, which is what keeps grouping off the dashboard's cost.
      const [result, loaded] = await Promise.all([
        apiRequest<{ quizzes: QuizSummary[] }>(`/workspaces/${workspaceId}/quizzes`),
        folderApi.list(workspaceId),
      ]);
      setQuizzes(result.quizzes);
      setFolders(loaded);
      // A selection outlives what it pointed at in two ways: the folder is
      // deleted, or the last unfiled poll is filed and that group stops
      // existing. Either one would leave the rail showing nothing with no tape
      // marked, so the selection is checked against the rows that just arrived
      // rather than against the ones already on screen.
      setFilter((current) => {
        if (current === null) return null;
        const keys = groupQuizzesByFolder(result.quizzes, loaded).map(groupKey);
        return keys.includes(current) ? current : null;
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load polls.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (naming || !returnFocus.current) return;
    returnFocus.current = false;
    newFolderRef.current?.focus();
  }, [naming]);

  /** Closes the naming field and hands focus back to the button it replaced. */
  function closeNaming() {
    returnFocus.current = true;
    setNaming(false);
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!workspaceId || !title.trim()) return;
    setError(null);
    setCreating(true);

    try {
      await apiRequest(`/workspaces/${workspaceId}/quizzes`, {
        method: 'POST',
        body: {
          title: title.trim(),
          ...(starter ? { starter } : {}),
          ...(createInFolder ? { folderId: createInFolder } : {}),
        },
      });
      track('pollCreated');
      setTitle('');
      setStarter('');
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create that poll.');
    } finally {
      setCreating(false);
    }
  }

  async function addFolder(event: FormEvent) {
    event.preventDefault();
    if (!workspaceId || !folderName.trim()) return;
    setError(null);
    setAddingFolder(true);

    try {
      await folderApi.create(workspaceId, folderName.trim());
      track('folderCreated');
      setFolderName('');
      closeNaming();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create that folder.');
    } finally {
      setAddingFolder(false);
    }
  }

  async function renameFolder(folder: Folder) {
    if (!workspaceId) return;
    const next = window.prompt('Rename this folder', folder.name);
    if (next === null || next.trim() === folder.name) return;

    setError(null);
    try {
      await folderApi.rename(workspaceId, folder.folderId, next.trim());
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not rename that folder.');
    }
  }

  /**
   * The confirmation says what actually happens, because the obvious fear is
   * the wrong one: nothing filed in the folder is deleted, it simply becomes
   * unfiled.
   */
  async function removeFolder(folder: Folder) {
    if (!workspaceId) return;
    const filed = quizzes.filter((quiz) => quiz.folderId === folder.folderId).length;
    const consequence =
      filed === 0
        ? ''
        : ` Its ${filed} ${filed === 1 ? 'poll' : 'polls'} will not be deleted — they become unfiled.`;
    if (!window.confirm(`Delete the folder “${folder.name}”?${consequence}`)) return;

    setError(null);
    try {
      await folderApi.remove(workspaceId, folder.folderId);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not delete that folder.');
    }
  }

  async function moveQuiz(quiz: QuizSummary, folderId: string) {
    setError(null);
    setMovingId(quiz.quizId);

    try {
      await folderApi.setQuizFolder(quiz.quizId, folderId === UNFILED ? null : folderId);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not move that poll.');
    } finally {
      setMovingId(null);
    }
  }

  /**
   * The create form follows the rail: a poll made while standing in a folder
   * belongs in it. The form keeps its own select, so the default is a starting
   * point rather than a rule.
   */
  function selectFolder(next: string | null) {
    setFilter(next);
    setCreateIn(next ?? UNFILED);
  }

  /**
   * Story #48. The copy opens in the builder rather than landing quietly in
   * this grid: the title it arrives with is a placeholder, and the editor is
   * where it can be renamed. The original's title travels along so the notice
   * there can name what this was copied from — the copy's own title cannot say
   * it once the author starts typing over it.
   */
  async function duplicate(quiz: QuizSummary) {
    setError(null);
    setDuplicatingId(quiz.quizId);

    try {
      const copy = await quizApi.duplicate(quiz.quizId);
      track('pollDuplicated');
      navigate(`/polls/${copy.quizId}`, { state: { duplicatedFrom: quiz.title } });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not duplicate that poll.');
      setDuplicatingId(null);
    }
  }

  if (!active) return <p className="muted">No workspace selected.</p>;

  function card(quiz: QuizSummary) {
    // A folder can be deleted without rewriting the polls filed in it, so the
    // dangling id reads as unfiled here exactly as it does in the grouping.
    const filedIn =
      quiz.folderId && folders.some((f) => f.folderId === quiz.folderId) ? quiz.folderId : UNFILED;

    return (
      // An article rather than the wrapping link it used to be: the card
      // now holds a button, and a button inside an anchor is neither
      // valid HTML nor operable. The title's link is stretched over the
      // card in CSS instead, so the whole surface still opens the poll
      // while the button keeps its own hit area.
      <article key={quiz.quizId} className="quiz-card">
        <div className="quiz-card-head">
          <h2>
            <Link to={`/polls/${quiz.quizId}`} className="quiz-card-link">
              {quiz.title}
            </Link>
          </h2>
          {quiz.sample || quiz.status === 'live' ? (
            // Outside the h2, so the link's accessible name stays the title
            // alone rather than "All-hands kickoff Sample".
            <span className="quiz-card-tags">
              {quiz.sample ? <span className="badge">Sample</span> : null}
              {quiz.status === 'live' ? <span className="lamp">Live</span> : null}
            </span>
          ) : null}
        </div>
        <CodeCells code={quiz.joinCode} size="sm" />
        <p className="muted">
          {quiz.screenCount} {quiz.screenCount === 1 ? 'screen' : 'screens'} · {quiz.questionCount}{' '}
          {quiz.questionCount === 1 ? 'question' : 'questions'}
        </p>
        <p className="muted">
          {quiz.totalVotes > 0
            ? `${quiz.totalVotes} ${quiz.totalVotes === 1 ? 'vote' : 'votes'} collected`
            : 'No votes yet'}
        </p>
        {canWrite ? (
          <div className="quiz-card-actions">
            {folders.length > 0 ? (
              <select
                // Every card carries one of these, so the poll's name is what
                // tells them apart in a screen reader's list of controls.
                aria-label={`Folder for ${quiz.title}`}
                className={filedIn ? 'folder-chip is-filed' : 'folder-chip'}
                value={filedIn}
                disabled={movingId !== null}
                onChange={(e) => void moveQuiz(quiz, e.target.value)}
              >
                <option value={UNFILED}>Unfiled</option>
                {folders.map((folder) => (
                  <option key={folder.folderId} value={folder.folderId}>
                    {folder.name}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              className="secondary quiz-card-duplicate"
              aria-label={`Duplicate ${quiz.title}`}
              disabled={duplicatingId !== null}
              onClick={() => void duplicate(quiz)}
            >
              {duplicatingId === quiz.quizId ? 'Duplicating…' : 'Duplicate'}
            </button>
          </div>
        ) : null}
      </article>
    );
  }

  const groups = groupQuizzesByFolder(quizzes, folders);
  const shown =
    filter === null ? quizzes : (groups.find((group) => groupKey(group) === filter)?.quizzes ?? []);
  // Rename and Delete act on a folder, so they only stand up for a real one —
  // never for All, and never for the unfiled remainder, which is not a row.
  const activeFolder = folders.find((folder) => folder.folderId === filter) ?? null;
  // A workspace with no folders still needs somewhere to make its first one,
  // but a workspace with nothing at all is reading the empty state instead.
  const showRail = folders.length > 0 || (canWrite && quizzes.length > 0);

  return (
    <section>
      <h1>Polls</h1>
      <p className="muted">The polls in {active.name}.</p>

      <FormError message={error} />

      {canWrite ? (
        <>
          <form className="panel inline-form" onSubmit={create}>
            <input
              ref={titleAnchor}
              aria-label="New poll title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Name your next poll…"
              required
            />
            <select
              aria-label="Start from"
              value={starter}
              onChange={(e) => setStarter(e.target.value)}
            >
              <option value="">Blank poll</option>
              {STARTER_NAMES.map((name) => (
                <option key={name} value={name}>
                  {STARTERS[name].label}
                </option>
              ))}
            </select>
            {folders.length > 0 ? (
              <select
                aria-label="Folder for the new poll"
                value={createInFolder}
                onChange={(e) => setCreateIn(e.target.value)}
              >
                <option value={UNFILED}>Unfiled</option>
                {folders.map((folder) => (
                  <option key={folder.folderId} value={folder.folderId}>
                    {folder.name}
                  </option>
                ))}
              </select>
            ) : null}
            <button ref={submitAnchor} type="submit" disabled={creating || !title.trim()}>
              {creating ? 'Creating…' : 'Create poll'}
            </button>
          </form>
          {isStarterName(starter) ? <p className="muted">{STARTERS[starter].description}</p> : null}
        </>
      ) : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          {showRail ? (
            <div className="folder-rail">
              {folders.length > 0 ? (
                <div className="folder-tapes" role="group" aria-label="Filter by folder">
                  <button
                    type="button"
                    className="folder-tape"
                    aria-pressed={filter === null}
                    aria-label={`All, ${quizzes.length} ${quizzes.length === 1 ? 'poll' : 'polls'}`}
                    onClick={() => selectFolder(null)}
                  >
                    <span className="folder-tape-name">All</span>
                    <span className="folder-tape-count">{quizzes.length}</span>
                  </button>
                  {groups.map((group) => {
                    const key = groupKey(group);
                    const name = group.folder ? group.folder.name : 'Unfiled';
                    const count = group.quizzes.length;
                    return (
                      <button
                        key={key || 'unfiled'}
                        type="button"
                        className="folder-tape"
                        aria-pressed={filter === key}
                        // The count is part of the label rather than loose text
                        // beside it, so a screen reader reads "All-hands, 4
                        // polls" instead of two unrelated announcements.
                        aria-label={`${name}, ${count} ${count === 1 ? 'poll' : 'polls'}`}
                        title={name}
                        onClick={() => selectFolder(key)}
                      >
                        <span className="folder-tape-name">{name}</span>
                        <span className="folder-tape-count">{count}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {canWrite ? (
                <div className="folder-rail-actions">
                  {activeFolder ? (
                    <>
                      <button
                        type="button"
                        className="secondary"
                        aria-label={`Rename ${activeFolder.name}`}
                        onClick={() => void renameFolder(activeFolder)}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        aria-label={`Delete ${activeFolder.name}`}
                        onClick={() => void removeFolder(activeFolder)}
                      >
                        Delete
                      </button>
                    </>
                  ) : null}
                  {naming ? (
                    <form className="folder-new-form" onSubmit={addFolder}>
                      <input
                        aria-label="New folder name"
                        // The rail swaps a button for this field, so the caret
                        // has to arrive with it.
                        autoFocus
                        value={folderName}
                        onChange={(e) => setFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') closeNaming();
                        }}
                        placeholder="Folder name"
                      />
                      <button type="submit" disabled={addingFolder || !folderName.trim()}>
                        {addingFolder ? 'Adding…' : 'Create folder'}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={closeNaming}
                        disabled={addingFolder}
                      >
                        Cancel
                      </button>
                    </form>
                  ) : (
                    <button
                      ref={newFolderRef}
                      type="button"
                      className="secondary"
                      onClick={() => setNaming(true)}
                    >
                      New folder
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {quizzes.length === 0 ? (
            <div className="panel empty-state">
              <h2>No polls yet</h2>
              <p className="muted">
                {canWrite
                  ? 'Create your first poll above, add a few questions, then put it on the big screen.'
                  : 'Nobody has created a poll in this workspace yet.'}
              </p>
              <button type="button" className="secondary" onClick={startGuide}>
                Show me around
              </button>
            </div>
          ) : shown.length === 0 ? (
            <p className="muted folder-empty">
              Nothing filed in {activeFolder?.name} yet
              {canWrite ? ' — set a poll’s folder from its card.' : '.'}
            </p>
          ) : (
            <>
              {shown.some((quiz) => quiz.sample) ? (
                <p className="muted sample-note">
                  Polls marked Sample are examples of what FloorPoll can do.
                  {canWrite
                    ? ' Present one as it is, edit it, duplicate it, or delete it when you’re done.'
                    : null}
                </p>
              ) : null}
              <div className="quiz-grid">{shown.map((quiz) => card(quiz))}</div>
            </>
          )}
        </>
      )}
    </section>
  );
}
